var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import TreeView from 'devextreme/ui/tree_view';
import { v4 as uuid4 } from 'uuid';
import { updateWhenLocaleChanges } from '@lit/localize';
import Tooltip from 'devextreme/ui/tooltip';
/**
 * Represents the hierarchy with a treeView
 */
let DinapsisTreeView = class DinapsisTreeView extends LitElement {
    constructor() {
        super();
        this.treeView = undefined;
        this.idItemSelected = '';
        /**
         * A property that is used to set the title of the tree view.
         */
        this.title = '';
        /**
         * A property that is used to set the option showCheckBoxesMode of devExtreme TreeView.
         * @options none | normal | selectAll
         */
        this.showCheckBoxesMode = 'selectAll';
        /**
         * A property that is used to set the text for selectAll
         */
        this.selectAllText = 'Select All';
        /**
         * A property that is used to set the option selectionNode of devExtreme TreeView.
         * @options single | multiple
         */
        this.selectionMode = 'multiple';
        /**
         * A property that is used to set the option selectNodesRecursive of devExtreme TreeView.
         */
        this.selectNodesRecursive = true;
        /**
         * A property that is used to set the data that is used to populate the tree view.
         */
        this.data = [];
        /**
         * A property that is used to set the id of the div that the tree view is rendered in.
         */
        this.treeViewId = 'dinapsis-tree-view';
        /**
         * A property that is used to set enable the search input
         */
        this.searchEnabled = false;
        /**
         * A property that is used to modify the search mode
         */
        this.searchMode = 'contains';
        /**
         * A property that is used to intialize search with default value
         */
        this.searchDefaultValue = '';
        /**
         * A property that is used to set a delay to filter node list
         */
        this.searchTimeout = 0;
        /**
         * A property to set the max height of the tree view
         */
        this.maxHeight = 'calc(100vh - 65px)';
        /**
         * Private variable for set random id
         */
        this.randomId = uuid4();
        updateWhenLocaleChanges(this);
    }
    /**
     * The createRenderRoot function is a function that is called by the lit-html library to tell the
     * component where to render the html.
     * @returns The element itself.
     */
    createRenderRoot() {
        return this;
    }
    updated(changedProperties) {
        if (changedProperties.has('data')) {
            const oldValue = changedProperties.get('data');
            const newValue = this.data;
            if (oldValue !== newValue && this.treeView) {
                if (this.showCheckBoxesMode === 'none') {
                    this.idItemSelected = '';
                }
                this.treeView.option('dataSource', newValue);
                this.scrollToItemSelected();
            }
        }
        if (changedProperties.has('searchEnabled')
            || changedProperties.has('searchMode')
            || changedProperties.has('searchDefaultValue')
            || changedProperties.has('searchTimeout')) {
            this.setSearch();
        }
    }
    /**
     * Scrolls the selected item into view based on the `data-item-id` ID.
     */
    scrollToItemSelected() {
        if (!this.idItemSelected)
            return;
        const selectedDOMItem = document.querySelector(`[data-item-id="${this.idItemSelected}"]`);
        if (!selectedDOMItem)
            return;
        selectedDOMItem.scrollIntoView();
    }
    /**
     * If the searchEnabled property is true, then the searchMode property is set.
     */
    setSearch() {
        if (!this.treeView)
            return;
        // searchEnabled
        this.treeView.option('searchEnabled', this.searchEnabled);
        if (!this.searchEnabled)
            return;
        // Search Mode
        this.treeView.option('searchMode', this.searchMode);
        // Default Search Value
        this.treeView.option('searchValue', this.searchDefaultValue);
        // Search Timeout
        this.treeView.option('searchTimeout', this.searchTimeout);
    }
    /**
     * The above function is a lifecycle hook that is called when the component is first updated.
     * It is used to initialize the tree view.
     */
    firstUpdated() {
        this.checkProperties();
        const container = document.querySelector(`.${this.treeViewId}-${this.randomId}`);
        if (container) {
            // Instance Creation
            this.treeView = new TreeView(container);
            // Basic config
            this.treeView.option('noDataText', '');
            // Select All Mode
            this.treeView.option('showCheckBoxesMode', this.showCheckBoxesMode);
            if (this.showCheckBoxesMode === 'selectAll') {
                this.treeView.option('selectAllText', this.selectAllText);
                // Event for Select All option
                this.treeView.option('onSelectAllValueChanged', (e) => this.changeAllSelection(e));
            }
            // Check modes
            this.treeView.option('selectByClick', true);
            this.treeView.option('selectionMode', this.selectionMode);
            this.treeView.option('selectNodesRecursive', this.selectNodesRecursive);
            // OnChange Event
            if (this.showCheckBoxesMode !== 'none') {
                this.treeView.option('onSelectionChanged', (e) => {
                    this.changeSelection(e);
                    this.getDataItems();
                });
            }
            // OnItemRendered Event
            this.treeView.option('onItemRendered', (event) => {
                var _a;
                this.removeCheckboxSelectable(event);
                // add Event listener Mouse Enter to create a tooltip or not
                (_a = document.getElementById(event.itemData.id)) === null || _a === void 0 ? void 0 : _a.addEventListener('mouseenter', (mouseEvent) => {
                    const { id, innerText } = mouseEvent.target;
                    // Check if tooltip exist for this element
                    const tooltipDiv = document.getElementById(`${id}-tooltip`);
                    if (tooltipDiv)
                        return;
                    // Generate the tooltip if is needed
                    this.generateTooltip(id, innerText);
                    // Get the tooltipCreated
                    const tooltipDivOK = document.getElementById(`${id}-tooltip`);
                    if (!tooltipDivOK)
                        return;
                    // If tootltip exist, activate it
                    const instancetest = Tooltip.getInstance(tooltipDivOK);
                    instancetest.show();
                });
            });
            // onContentReady Event
            this.treeView.option('onContentReady', (e) => this.removeStyleSelected(e));
            // OnItemClick Event
            this.treeView.option('onItemClick', (e) => {
                this.clickEvent(e);
                if (e.itemData.selectable === false) {
                    e.event.preventDefault();
                }
                if (this.showCheckBoxesMode === 'none'
                    && e.itemData.selectable
                    && this.selectionMode === 'single') {
                    e.event.preventDefault();
                    const oldSelected = this.idItemSelected;
                    this.idItemSelected = e.itemData.id;
                    if (oldSelected && this.treeView) {
                        this.treeView.instance().element()
                            .getElementsByClassName('tree-view-item-single-selected')[0]
                            .classList.remove('tree-view-item-single-selected');
                        this.treeView.instance().element()
                            .getElementsByClassName('dx-treeview-item-selected')[0]
                            .classList.remove('dx-treeview-item-selected');
                    }
                    e.itemElement.getElementsByClassName('tree-view-item')[0]
                        .classList.add('tree-view-item-single-selected');
                    e.itemElement.classList.add('dx-treeview-item-selected');
                }
            });
            // DataSource
            this.treeView.option('dataSource', this.data);
            // DataSource
            this.treeView.option('itemTemplate', (data) => {
                if (this.idItemSelected === '' && data.selected === true && this.showCheckBoxesMode === 'none') {
                    this.idItemSelected = data.id;
                }
                const icon = data.icon
                    ? `<span class="material-icons-round tree-view-item-icon ">
              ${data.icon}
            </span>`
                    : '';
                const spanDataTextClass = (data.selectable || data.selectable === undefined)
                    ? 'tree-line-text tree-line-text-selectable'
                    : 'tree-line-text';
                const itemClass = (data.selected && this.selectionMode === 'single')
                    ? 'tree-view-item tree-view-item-single-selected'
                    : 'tree-view-item';
                return `
        <div class="tree-line-container" id="${data.id}">
          <div class="${itemClass}">
            ${icon}
            <span id="${data.id}-text" class="${spanDataTextClass}">${data.text}</span>
          </div>
        </div>`;
            });
        }
    }
    /**
     * Generate Tooltip div.
     * @param id id of parentElement.
     * @param text for tooltip
     */
    generateTooltip(id, text) {
        const tooltipContainer = document.getElementById(`${id}-text`);
        const parentContainer = document.getElementById(`${id}`);
        if (!tooltipContainer || !parentContainer)
            return;
        // Check if elipsis exist to create the tooltip
        const nodeItem = parentContainer.childNodes[1];
        const elipsisActive = nodeItem.offsetWidth < nodeItem.scrollWidth;
        if (!elipsisActive)
            return;
        // Create an element for Tooltip
        const newTooltip = document.createElement('div');
        newTooltip.className = 'tree-view-tooltip-container';
        newTooltip.id = `${id}-tooltip`;
        // Create a devextreme Tooltip
        const tooltip = new Tooltip(newTooltip);
        // Add Tooltips options
        tooltip.option('target', tooltipContainer);
        tooltip.option('showEvent', 'mouseenter');
        tooltip.option('hideEvent', 'mouseleave');
        tooltip.option('contentTemplate', () => text);
        tooltipContainer === null || tooltipContainer === void 0 ? void 0 : tooltipContainer.append(newTooltip);
    }
    /**
     * If the item is not selectable, remove the checkbox and padding from the item.
     * @param {any} e - any - the event object
     */
    removeCheckboxSelectable(e) {
        if (e.itemData.selectable === false || this.showCheckBoxesMode === 'none') {
            const container = e.itemElement.parentNode;
            container.classList.remove('dx-treeview-item-with-checkbox');
            const checkbox = container.querySelector('.dx-checkbox');
            const itemContainer = container.querySelector('.dx-item');
            if (checkbox)
                checkbox.style.display = 'none';
            if (itemContainer && e.itemData.selectable === false) {
                itemContainer.classList.add('dx-treeview-not-selectable-item');
            }
            if (this.showCheckBoxesMode === 'none'
                && e.itemData.selected) {
                itemContainer.classList.remove('.dx-treeview-item');
                itemContainer.classList.add('dx-treeview-item-selected');
            }
        }
    }
    /**
     * If showCheckBoxesMode is none remove selected items styles
     * @param {any} e - any - the event object
     */
    removeStyleSelected(e) {
        if (this.treeView && this.showCheckBoxesMode === 'none') {
            const container = e.element;
            const selectedElement = container.querySelectorAll('.dx-state-selected');
            selectedElement.forEach((element) => {
                if (element) {
                    element.classList.remove('dx-state-selected');
                }
            });
        }
    }
    /**
     * It takes the selected nodes, filters out the ones that are not selectable,
     * and returns the remaining selected nodes.
     * @param {any} e - any - the event object
     * @returns The filteredSelected is an array of the selected keys that are selectable.
     */
    filteredNodesSelection(e) {
        const { component } = e;
        const selectedKeys = component.getSelectedNodeKeys();
        const selected = component.getSelectedNodes();
        const itemsNotSelectable = selected
            .filter((i) => i.itemData.selectable === false)
            .map((i) => i.key);
        const filteredSelected = selectedKeys.filter((i) => !itemsNotSelectable.includes(i));
        return filteredSelected;
    }
    /**
     * When the user clicks on a tree view item, dispatch a custom event called
     * 'select-item' and pass the tree view item that was clicked on as the detail.
     * @param {any} e - any - the event object
     */
    changeSelection(e) {
        const filteredSelected = this.filteredNodesSelection(e);
        this.dispatchEvent(new CustomEvent('select-item', { detail: filteredSelected }));
    }
    /**
   * When the user clicks on a tree view item, dispatch a custom event called
   * 'get-data-items' and pass all the tree view data.
   */
    getDataItems() {
        this.dispatchEvent(new CustomEvent('get-data-items', { detail: this.data }));
    }
    /**
     * When the user clicks the checkbox in the header of the treeView,
     * this function will fire and dispatch an event to the parent component.
     * @param {any} e - any - this is the event object that is passed to the function.
     */
    changeAllSelection(e) {
        const filteredSelected = this.filteredNodesSelection(e);
        this.dispatchEvent(new CustomEvent('select-all-items', { detail: filteredSelected }));
    }
    /**
     * The clickEvent function is called when a user clicks on a menu item.
     * It then dispatches a custom event called click-item.
     * The detail of the event is the key of the menu item that was clicked
     * @param {any} e - any - the event object
     */
    clickEvent(e) {
        const { itemData } = e;
        if (!itemData.selectable)
            return;
        this.removePreviousSelectedItems(this.data);
        itemData.selected = !itemData.selected;
        this.dispatchEvent(new CustomEvent('click-item', { detail: itemData }));
    }
    /**
     * Remove all selected items from data array when selection mode is 'single'.
     * @param data Array of TreeViewItems
     */
    removePreviousSelectedItems(data) {
        if (data.length === 0 || this.selectionMode === 'multiple')
            return;
        data.forEach((item) => {
            // eslint-disable-next-line no-param-reassign
            item.selected = false;
            if (item.items) {
                this.removePreviousSelectedItems(item.items);
            }
        });
    }
    /**
     * When the component is created check if all properties are correctly
     * @throws Will throw an error if @param title is empty string
     */
    checkProperties() {
        if (this.title === '') {
            throw new Error('Title property can not be an empty string');
        }
    }
    /**
     * If the title property is not an empty string,
     * then render the tree-view-title div with
     * the title property as the text. Otherwise, render an empty string.
     * @returns The return statement is returning a template literal.
     */
    render() {
        return html `
      <style>
        .dx-scrollable-container {
          max-height: ${this.maxHeight};
        }
      </style>
      <div class="tree-view-container" title="">
        <div class="tree-view-title noselect">${this.title}</div>
        <div
          class="dinapsis-tree-view ${this.treeViewId}-${this.randomId}"
          id="${this.treeViewId}"
        ></div>
      </div>
    `;
    }
};
__decorate([
    property({ type: String })
], DinapsisTreeView.prototype, "title", void 0);
__decorate([
    property()
], DinapsisTreeView.prototype, "showCheckBoxesMode", void 0);
__decorate([
    property({ type: String })
], DinapsisTreeView.prototype, "selectAllText", void 0);
__decorate([
    property({ type: String })
], DinapsisTreeView.prototype, "selectionMode", void 0);
__decorate([
    property({ type: Boolean })
], DinapsisTreeView.prototype, "selectNodesRecursive", void 0);
__decorate([
    property({ type: Array() })
], DinapsisTreeView.prototype, "data", void 0);
__decorate([
    property({ type: String })
], DinapsisTreeView.prototype, "treeViewId", void 0);
__decorate([
    property({ type: Boolean })
], DinapsisTreeView.prototype, "searchEnabled", void 0);
__decorate([
    property({ type: String })
], DinapsisTreeView.prototype, "searchMode", void 0);
__decorate([
    property({ type: String })
], DinapsisTreeView.prototype, "searchDefaultValue", void 0);
__decorate([
    property({ type: Number })
], DinapsisTreeView.prototype, "searchTimeout", void 0);
__decorate([
    property({ type: String })
], DinapsisTreeView.prototype, "maxHeight", void 0);
DinapsisTreeView = __decorate([
    customElement('dinapsis-tree-view')
], DinapsisTreeView);
export { DinapsisTreeView };
//# sourceMappingURL=dinapsis-tree-view.js.map